/**
 * useCustomerOrders Hook
 * React Query hook for fetching paginated customer orders with filters
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { customerOrderApi } from '../api/orderApi';
import type {
  CustomerOrdersResponse,
  CustomerOrderFilters,
  PaginationParams,
} from '../types/order.types';

// ============================================
// QUERY KEY FACTORY
// ============================================

/**
 * Query key factory for customer orders
 */
export const customerOrderKeys = {
  all: ['customer-orders'] as const,
  lists: () => [...customerOrderKeys.all, 'list'] as const,
  list: (pagination: PaginationParams, filters?: CustomerOrderFilters) =>
    [...customerOrderKeys.lists(), pagination, filters] as const,
  details: () => [...customerOrderKeys.all, 'detail'] as const,
  detail: (id: number) => [...customerOrderKeys.details(), id] as const,
  status: (id: number) => [...customerOrderKeys.all, 'status', id] as const,
  delivery: (id: number) => [...customerOrderKeys.all, 'delivery', id] as const,
};

// ============================================
// HOOK CONFIGURATION
// ============================================

/**
 * Configuration options for useCustomerOrders hook
 */
export interface UseCustomerOrdersOptions {
  pagination: PaginationParams;
  filters?: CustomerOrderFilters;
  enabled?: boolean;
  refetchInterval?: number | false;
}

// ============================================
// MAIN HOOK
// ============================================

/**
 * Hook for fetching customer's orders with pagination and filters
 *
 * @param options - Query options (pagination, filters, enabled state)
 * @returns React Query result with orders data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCustomerOrders({
 *   pagination: { page: 0, size: 20 },
 *   filters: { statuses: ['PENDING', 'CONFIRMED'] }
 * });
 * ```
 */
export function useCustomerOrders(
  options: UseCustomerOrdersOptions
): UseQueryResult<CustomerOrdersResponse, Error> {
  const { pagination, filters, enabled = true, refetchInterval = false } = options;

  return useQuery({
    queryKey: customerOrderKeys.list(pagination, filters),
    queryFn: () => customerOrderApi.getMyOrders(pagination, filters),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - orders don't change too frequently
    gcTime: 10 * 60 * 1000, // 10 minutes cache time (formerly cacheTime)
    refetchInterval,
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export default useCustomerOrders;
